import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { PhoneCall, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { business } from "@/data/content";
import { trackInquiryConversion } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const times = ["ASAP", "Morning", "Afternoon"];

export default function HeroCallback() {
  const [form, setForm] = useState({ name: "", phone: "", callback_time: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (form.phone.trim().length < 6) e.phone = "Please enter a valid mobile number.";
    if (!form.callback_time) e.callback_time = "Please choose a time.";
    if (!form.consent) e.consent = "Please provide consent.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/callbacks`, form);
      trackInquiryConversion();
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setErrors({ form: typeof d === "string" ? d : "Something went wrong. Please call us instead." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white/85 backdrop-blur-xl border border-pristine-aqua shadow-[0_16px_50px_rgba(15,58,112,0.14)] p-5 sm:p-6" data-testid="hero-callback-card">
      {done ? (
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4" data-testid="callback-success">
          <CheckCircle2 className="h-11 w-11 text-pristine-aquadeep mx-auto" />
          <p className="mt-3 font-heading text-lg font-semibold text-pristine-navy">We'll call you back</p>
          <p className="mt-1 text-sm text-pristine-navy/65">Our local team will be in touch. Urgent? <a href={business.phoneHref} className="text-pristine-blue font-semibold underline">Call now</a>.</p>
        </motion.div>
      ) : (
        <form onSubmit={submit} className="space-y-3.5" data-testid="hero-callback-form" noValidate>
          <div className="flex items-center gap-2">
            <PhoneCall className="h-4 w-4 text-pristine-aquadeep" />
            <p className="font-heading text-base font-semibold text-pristine-navy">Request a quick callback</p>
          </div>
          {/* honeypot */}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />

          <div>
            <Label htmlFor="cb-name" className="sr-only">Name</Label>
            <Input id="cb-name" data-testid="callback-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className={`h-11 rounded-xl bg-white ${errors.name ? "border-destructive" : "border-pristine-aqua"}`} />
            {errors.name && <p className="mt-1 text-xs text-destructive" data-testid="callback-error-name">{errors.name}</p>}
          </div>
          <div>
            <Label htmlFor="cb-phone" className="sr-only">Mobile number</Label>
            <Input id="cb-phone" data-testid="callback-phone" inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Mobile number" className={`h-11 rounded-xl bg-white ${errors.phone ? "border-destructive" : "border-pristine-aqua"}`} />
            {errors.phone && <p className="mt-1 text-xs text-destructive" data-testid="callback-error-phone">{errors.phone}</p>}
          </div>
          <div>
            <Select value={form.callback_time} onValueChange={(v) => set("callback_time", v)}>
              <SelectTrigger data-testid="callback-time" className={`h-11 rounded-xl bg-white ${errors.callback_time ? "border-destructive" : "border-pristine-aqua"}`}>
                <SelectValue placeholder="Preferred callback time" />
              </SelectTrigger>
              <SelectContent>
                {times.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            {errors.callback_time && <p className="mt-1 text-xs text-destructive" data-testid="callback-error-time">{errors.callback_time}</p>}
          </div>

          <div className="flex items-start gap-2.5">
            <Checkbox id="cb-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="callback-consent" className="mt-0.5 border-pristine-aquadeep data-[state=checked]:bg-pristine-blue" />
            <Label htmlFor="cb-consent" className="text-xs text-pristine-navy/70 leading-snug cursor-pointer font-normal">
              I consent to Pristine Water Systems contacting me about my enquiry.
            </Label>
          </div>
          {errors.consent && <p className="text-xs text-destructive" data-testid="callback-error-consent">{errors.consent}</p>}
          {errors.form && <p className="text-xs text-destructive text-center" data-testid="callback-error-form">{errors.form}</p>}

          <Button type="submit" disabled={submitting} data-testid="callback-submit" className="w-full rounded-full bg-pristine-blue hover:bg-pristine-bluehover text-white h-12 font-semibold">
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Request a Callback"}
          </Button>
        </form>
      )}
    </div>
  );
}
